import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

function PricingPage() {
  const navigate = useNavigate();
  const [annual, setAnnual] = useState(false);

  const plans = [
    {
      id: 'essential',
      name: 'Essential',
      price: 99,
      annualPrice: 990,
      color: 'from-blue-500 to-blue-600',
      borderColor: 'border-blue-200',
      accentColor: 'text-blue-600',
      bgAccent: 'bg-blue-50',
      popular: false,
      tagline: 'Perfect for teams starting their AI recruitment journey',
      highlight: '10 Job Requirements / month',
      highlightSub: 'Upload JD + Audio or Video file per requirement',
      breakdown: [
        { icon: '📄', label: 'JD Upload & AI Analysis', count: '10 reqs/mo' },
        { icon: '🎙️', label: 'Audio / Video Transcription', count: '10 reqs/mo' },
        { icon: '🤖', label: 'AI Recruiter Brief Document', count: 'Per requirement' },
        { icon: '📋', label: 'AI Candidate Screener', count: 'Per requirement' },
        { icon: '❓', label: 'Screening Questions (10)', count: 'Per requirement' },
      ],
      features: [
        'Upload JD + Audio or Video file',
        'AI extracts 11 requirement categories',
        'Auto-generated recruiter brief document',
        'Candidate screener with scoring guide',
        '10 targeted screening questions per req',
        'Duplicate detection — no wasted credits',
        'PDF & Word document downloads',
        'Email support',
      ],
    },
    {
      id: 'professional',
      name: 'Professional',
      price: 199,
      annualPrice: 1990,
      color: 'from-green-500 to-teal-500',
      borderColor: 'border-green-400',
      accentColor: 'text-green-700',
      bgAccent: 'bg-green-50',
      popular: true,
      tagline: 'For growing teams who need smarter intake conversations',
      highlight: '5 JD Uploads + 10 AI Intake Calls / month',
      highlightSub: '5 JD Uploads (Phase 1)  +  10 AI Intake Calls (Phase 2)',
      breakdown: [
        { icon: '📄', label: 'JD Upload & Analysis (Phase 1)', count: '5 reqs/mo' },
        { icon: '🤝', label: 'AI Intake Calls — Hiring Manager (Phase 2)', count: '10 calls/mo' },
        { icon: '🤖', label: 'AI Recruiter + Screener Docs', count: 'All reqs' },
        { icon: '📊', label: 'Advanced Requirement Intelligence', count: 'All reqs' },
        { icon: '👥', label: 'Team Collaboration', count: 'Unlimited users' },
      ],
      features: [
        'Everything in Essential',
        '5 JD + Audio/Video uploads (Phase 1)',
        '10 live AI intake calls with Hiring Managers (Phase 2)',
        'Jessica — AI interviewer conducts structured intake meetings',
        'Real-time transcript + auto-generated requirement docs',
        'Hiring Manager timezone awareness',
        'Advanced analytics dashboard',
           ],
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      price: 499,
      annualPrice: 4990,
      color: 'from-purple-500 to-indigo-600',
      borderColor: 'border-purple-300',
      accentColor: 'text-purple-700',
      bgAccent: 'bg-purple-50',
      popular: false,
      tagline: 'Full AI recruitment pipeline — from JD to screened candidate',
      highlight: '5 JD Uploads + 10 Intake Calls + 10 AI Interviews / month',
      highlightSub: '5 JD uploads  +  10 AI intake calls  +  10 AI candidate interviews',
      breakdown: [
        { icon: '📄', label: 'JD Upload & Analysis (Phase 1)', count: '5 reqs/mo' },
        { icon: '🤝', label: 'AI Intake Calls — Hiring Manager (Phase 2)', count: '10 calls/mo' },
        { icon: '🎯', label: 'AI Candidate Interviews (Phase 3)', count: '10 interviews/mo' },
        { icon: '🏆', label: 'Candidate Scoring 0–400 + Ranking', count: 'Per interview' },
        { icon: '📈', label: 'Percentile vs Cohort Benchmark', count: 'Per interview' },
      ],
      features: [
        'Everything in Professional',
        '10 AI candidate interviews/month (up to 90 min)',
        'Industry-leading 5-dimension scoring per problem',
        'Score 0–400 with percentile cohort ranking',
        'HM report + Recruiter screener — delivered instantly',
        'Proctoring signals & integrity confidence score',
        
      ],
    }
  ];

  const saving = (price, annualPrice) => (price * 12) - annualPrice;

  const handleSubscribe = (plan) => {
    navigate('/register', { state: { selectedPlan: plan } });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Quali5d Pricing — AI Recruitment Plans That Scale With Your Team</title>
        <meta name="description" content="Compare Quali5d Essential, Professional, and Enterprise plans. Per-company AI learning, transparent pricing, no hidden fees. Cut engineering interview time by 70%." />
        <link rel="canonical" href="https://www.quali5d.com/pricing" />
        <meta property="og:title" content="Quali5d Pricing — AI Recruitment Plans That Scale With Your Team" />
        <meta property="og:description" content="Compare Quali5d Essential, Professional, and Enterprise plans. Per-company AI learning, transparent pricing, no hidden fees. Cut engineering interview time by 70%." />
        <meta property="og:url" content="https://www.quali5d.com/pricing" />
        <meta name="twitter:title" content="Quali5d Pricing — AI Recruitment Plans That Scale With Your Team" />
        <meta name="twitter:description" content="Compare Quali5d Essential, Professional, and Enterprise plans. Per-company AI learning, transparent pricing, no hidden fees. Cut engineering interview time by 70%." />
      </Helmet>
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center cursor-pointer" onClick={() => navigate('/')}>
              <img src="/Reqt5d-Logo.png" alt="Quali5d" className="h-16" />
            </div>
            <button onClick={() => navigate('/')} className="text-gray-700 hover:text-blue-600 font-medium">
              ← Back to Home
            </button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="py-16 px-6 bg-gradient-to-br from-gray-900 via-blue-950 to-gray-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 px-4 py-2 rounded-full text-sm font-semibold mb-6">
            🏆 Up to 6× more cost-effective than specialist interview services — fully automated
          </div>
          <h1 className="text-5xl font-black mb-4 leading-tight">
            Transparent Pricing.<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-teal-400">
              Extraordinary Value.
            </span>
          </h1>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto mb-10">
            Replace hours of manual recruitment work with AI — from job description to screened candidate, fully automated.
          </p>

          {/* Billing Toggle */}
          <div className="inline-flex items-center bg-white/10 border border-white/20 rounded-2xl p-1.5">
            <button
              onClick={() => setAnnual(false)}
              className={`px-6 py-2.5 rounded-xl font-semibold text-sm transition-all ${!annual ? 'bg-white text-gray-900 shadow-lg' : 'text-gray-300 hover:text-white'}`}
            >
              Monthly
            </button>
            <button
              onClick={() => setAnnual(true)}
              className={`px-6 py-2.5 rounded-xl font-semibold text-sm transition-all flex items-center gap-2 ${annual ? 'bg-white text-gray-900 shadow-lg' : 'text-gray-300 hover:text-white'}`}
            >
              Annual
              <span className="bg-green-500 text-white text-xs px-2 py-0.5 rounded-full font-bold">Save 17%</span>
            </button>
          </div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-16 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto items-start">
            {plans.map((plan) => (
              <div
                key={plan.id}
                className={`relative bg-white rounded-2xl shadow-lg border-2 transition-all hover:shadow-2xl
                  ${plan.popular ? `${plan.borderColor} shadow-green-100 -mt-2` : 'border-gray-200'}`}
              >
                {/* Popular Badge */}
                {plan.popular && (
                  <div className="absolute -top-5 left-1/2 transform -translate-x-1/2 z-10">
                    <span className="bg-gradient-to-r from-green-500 to-teal-500 text-white px-6 py-2 rounded-full text-sm font-bold shadow-lg whitespace-nowrap">
                      ⭐ MOST POPULAR
                    </span>
                  </div>
                )}

                {/* Top gradient bar */}
                <div className={`h-2 rounded-t-2xl bg-gradient-to-r ${plan.color}`} />

                <div className="p-8">
                  {/* Name + tagline */}
                  <h3 className="text-2xl font-black text-gray-900">{plan.name}</h3>
                  <p className="text-sm text-gray-500 mt-1 mb-5 leading-snug">{plan.tagline}</p>

                  {/* Price */}
                  <div className="mb-5">
                    <div className="flex items-baseline gap-2">
                      <span className="text-5xl font-black text-gray-900">
                        ${annual ? Math.round(plan.annualPrice / 12) : plan.price}
                      </span>
                      <span className="text-gray-500 font-medium">/month</span>
                    </div>
                    {annual ? (
                      <p className="text-sm text-green-600 font-semibold mt-1">
                        ${plan.annualPrice}/year — save ${saving(plan.price, plan.annualPrice)}
                      </p>
                    ) : (
                      <p className="text-sm text-gray-400 mt-1">
                        or ${plan.annualPrice}/year (save ${saving(plan.price, plan.annualPrice)})
                      </p>
                    )}
                  </div>

                  {/* Requirement highlight box */}
                  <div className={`mb-5 p-4 ${plan.bgAccent} rounded-xl border ${plan.borderColor}`}>
                    <div className={`text-sm font-black ${plan.accentColor} mb-1`}>
                      📦 {plan.highlight}
                    </div>
                    <div className="text-xs text-gray-600 leading-relaxed">{plan.highlightSub}</div>
                  </div>

                  {/* Breakdown table */}
                  <div className="mb-5 space-y-1.5">
                    {plan.breakdown.map((item, i) => (
                      <div key={i} className="flex items-center justify-between py-1.5 border-b border-gray-100 last:border-0">
                        <span className="text-xs text-gray-600 flex items-center gap-1.5">
                          <span>{item.icon}</span> {item.label}
                        </span>
                        <span className={`text-xs font-bold ${plan.accentColor} whitespace-nowrap ml-2`}>{item.count}</span>
                      </div>
                    ))}
                  </div>

                  {/* Features */}
                  <ul className="space-y-2.5 mb-8">
                    {plan.features.map((feature, i) => (
                      <li key={i} className="flex items-start gap-2.5">
                        <svg className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                        </svg>
                        <span className={`text-sm ${feature.startsWith('Everything') ? 'font-bold text-gray-900' : 'text-gray-700'}`}>
                          {feature}
                        </span>
                      </li>
                    ))}
                  </ul>

                  {/* CTA */}
                  <button
                    onClick={() => handleSubscribe(plan)}
                    className={`w-full py-4 rounded-xl font-bold text-base transition-all shadow-md hover:shadow-xl hover:-translate-y-0.5 bg-gradient-to-r ${plan.color} text-white`}
                  >
                    Get Started →
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Comparison table */}
          <div className="mt-16 max-w-4xl mx-auto bg-gradient-to-br from-gray-900 to-blue-950 rounded-2xl p-8 text-white">
            <h3 className="text-2xl font-black text-center mb-2">How We Compare</h3>
            <p className="text-center text-gray-400 text-sm mb-8">Against traditional recruitment and specialist interview platforms</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr>
                    <th className="text-left text-gray-400 font-semibold pb-3 pr-4 border-b border-white/10">Feature</th>
                    <th className="text-center text-blue-300 font-semibold pb-3 px-4 border-b border-white/10">Traditional</th>
                    <th className="text-center text-orange-300 font-semibold pb-3 px-4 border-b border-white/10">Specialist Platforms</th>
                    <th className="text-center text-green-400 font-bold pb-3 px-4 border-b border-white/10">Quali5d ✦</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['Intake call automation',       '❌ Manual',    '❌ Manual',   '✅ AI (Jessica)'],
                    ['AI candidate interview',        '❌',           '✅ Human',    '✅ AI (Alex)'],
                    ['Cost per interview',            '—',            '$300',        '$50 equiv.'],
                    ['Recruiter doc generation',      '❌ Manual',    '❌',          '✅ Instant'],
                    ['Structured scoring 0–400',      '❌',           '✅',          '✅'],
                    ['Proctoring & integrity score',  '❌',           '⚠️ Basic',    '✅ Advanced'],
                    ['HM + Recruiter reports',        '❌',           '✅',          '✅ Both auto'],
                    ['Turnaround time',               '2–5 days',     '24 hrs',      '⚡ Minutes'],
                    ['Monthly subscription model',    '❌',           '❌ Per hire', '✅'],
                  ].map(([feat, trad, specialist, ours], i) => (
                    <tr key={i} className="border-b border-white/5 last:border-0">
                      <td className="text-gray-400 py-2.5 pr-4 text-xs">{feat}</td>
                      <td className="text-center text-gray-300 py-2.5 px-4 text-xs">{trad}</td>
                      <td className="text-center text-orange-200 py-2.5 px-4 text-xs">{specialist}</td>
                      <td className="text-center text-green-400 py-2.5 px-4 text-xs font-semibold">{ours}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Add-ons */}
          <div className="mt-14 max-w-5xl mx-auto">
            <div className="bg-white rounded-2xl shadow-lg p-8 border-2 border-gray-200">
              <div className="mb-6">
                <h3 className="text-2xl font-black text-gray-900">Need More Capacity?</h3>
                <p className="text-gray-500 mt-1 text-sm">Purchase add-on packs anytime from your dashboard. Valid for 30 days from purchase.</p>
              </div>

              {/* Phase 1 Add-ons */}
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-sm font-bold text-blue-700 bg-blue-50 border border-blue-200 px-3 py-1 rounded-full">📄 Phase 1 — JD Upload Packs</span>
                  <span className="text-xs text-gray-500">Available on all plans</span>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { name: 'Starter', count: '+5 uploads',  price: 19, key: 'addon_p1_starter' },
                    { name: 'Growth',  count: '+10 uploads', price: 29, key: 'addon_p1_growth'  },
                    { name: 'Scale',   count: '+20 uploads', price: 49, key: 'addon_p1_scale'   },
                  ].map((pack) => (
                    <div key={pack.key} className="border-2 border-gray-200 rounded-xl p-4 hover:border-blue-400 transition-all text-center">
                      <div className="text-sm font-bold text-gray-700 mb-1">{pack.name}</div>
                      <div className="text-xs text-blue-600 font-semibold mb-2">{pack.count}</div>
                      <div className="text-2xl font-black text-gray-900">${pack.price}</div>
                      <div className="text-xs text-gray-400 mt-0.5">one-time</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Phase 2 Add-ons */}
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-sm font-bold text-green-700 bg-green-50 border border-green-200 px-3 py-1 rounded-full">🤝 Phase 2 — Intake Call Packs</span>
                  <span className="text-xs text-gray-500">Professional &amp; Enterprise only</span>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { name: 'Starter', count: '+5 calls',  price: 39,  key: 'addon_p2_starter' },
                    { name: 'Growth',  count: '+10 calls', price: 79,  key: 'addon_p2_growth'  },
                    { name: 'Scale',   count: '+20 calls', price: 149, key: 'addon_p2_scale'   },
                  ].map((pack) => (
                    <div key={pack.key} className="border-2 border-gray-200 rounded-xl p-4 hover:border-green-400 transition-all text-center">
                      <div className="text-sm font-bold text-gray-700 mb-1">{pack.name}</div>
                      <div className="text-xs text-green-600 font-semibold mb-2">{pack.count}</div>
                      <div className="text-2xl font-black text-gray-900">${pack.price}</div>
                      <div className="text-xs text-gray-400 mt-0.5">one-time</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Phase 3 Add-ons */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-sm font-bold text-purple-700 bg-purple-50 border border-purple-200 px-3 py-1 rounded-full">🎯 Phase 3 — Interview Packs</span>
                  <span className="text-xs text-gray-500">Enterprise only</span>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { name: 'Starter', count: '+5 interviews',  price: 149, key: 'addon_p3_starter' },
                    { name: 'Growth',  count: '+10 interviews', price: 249, key: 'addon_p3_growth'  },
                    { name: 'Scale',   count: '+25 interviews', price: 499, key: 'addon_p3_scale'   },
                  ].map((pack) => (
                    <div key={pack.key} className="border-2 border-gray-200 rounded-xl p-4 hover:border-purple-400 transition-all text-center">
                      <div className="text-sm font-bold text-gray-700 mb-1">{pack.name}</div>
                      <div className="text-xs text-purple-600 font-semibold mb-2">{pack.count}</div>
                      <div className="text-2xl font-black text-gray-900">${pack.price}</div>
                      <div className="text-xs text-gray-400 mt-0.5">one-time</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* FAQ */}
          <div className="mt-16 max-w-3xl mx-auto">
            <h3 className="text-3xl font-black text-center text-gray-900 mb-8">Frequently Asked Questions</h3>
            <div className="space-y-4">
              {[
                {
                  q: 'What counts as a "requirement"?',
                  a: 'One requirement = one job role processed through the system. Phase 1: one JD + audio/video upload. Phase 2: one AI intake call with a Hiring Manager. Phase 3 AI candidate interviews are counted separately (10 included in Enterprise).'
                },
                {
                  q: 'Do unused requirements roll over?',
                  a: 'Monthly plan requirements reset each billing cycle and do not roll over. Purchased add-on packs are valid for 30 days from purchase date.'
                },
                {
                  q: 'How long can a Phase 3 AI candidate interview run?',
                  a: 'AI interviews are configurable from 30 to 90 minutes depending on role complexity. Alex manages timing automatically and wraps up gracefully within the limit.'
                },
                {
                  q: 'How does Quali5d compare to specialist interview platforms?',
                  a: 'Specialist interview platforms typically charge $200–$400 per interview using human interviewers. Quali5d Enterprise gives you 10 AI interviews at $499/month — equivalent to $50/interview — with faster turnaround, consistent 0–400 scoring, full report automation, and proctoring.'
                },
                {
                  q: 'Can I upgrade or downgrade plans?',
                  a: 'Yes, anytime. Upgrades take effect immediately. Downgrades apply at the start of your next billing cycle.'
                },
                {
                  q: 'Is there a free trial?',
                  a: 'Quali5d is offered exclusively as a paid subscription service. No free trials or complimentary access are provided. Contact us at admin@uni5d.ai to learn more about our plans.'
                },
              ].map((faq, i) => (
                <div key={i} className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                  <h4 className="font-black text-gray-900 mb-2">{faq.q}</h4>
                  <p className="text-gray-600 text-sm leading-relaxed">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 py-8 mt-8">
        <div className="max-w-7xl mx-auto px-6 text-center text-gray-500 text-sm">
          © 2026 Quali5d — Uni5d Technology Solutions LLC · Powered by AI
        </div>
      </footer>
    </div>
  );
}

export default PricingPage;